import { useContext, useSyncExternalStore } from "react";
import { DropdownMenu } from "@radix-ui/themes";
import { HelpCircle } from "lucide-react";
import { GlobalContext } from "./GlobalContext";
import { useIsMobile } from "./hooks/useMediaQuery";
import { canSeeTab } from "./tabPermissions";
import { emitter } from "./UtilityFunctions";
import {
    TOURS,
    subscribeTours,
    availableTours,
    startRegisteredTour,
    requestTour,
} from "./tourRegistry";

const ITEM = "px-3 py-2 text-sm text-gray-700 hover:bg-[var(--theme-accent-tint)] hover:text-[var(--theme-accent-ink)] rounded cursor-pointer transition-colors";
const LABEL = "px-3 pt-2 pb-1 text-xs font-medium text-[var(--theme-muted)]";

/**
 * The one place a user goes looking for help.
 *
 * Before this, the app overview started by clicking the company logo — an
 * affordance nothing announced — and every other tour hung off a button that
 * parks itself off-screen until you hover a two-pixel strip at the left edge.
 * Both are gone; this menu lists what is actually startable, right now.
 *
 * Desktop only, on the same grounds as JoyridePortal: the steps target chrome
 * (#tab-*, the settings rail, the sheet footer) that the mobile shell does not
 * render, so an entry point on a phone would only ever lead to a dead tour.
 */
export default function HelpMenu({ onOpenSecurity }) {
    const isMobile = useIsMobile();
    const { can } = useContext(GlobalContext);
    const registered = useSyncExternalStore(subscribeTours, availableTours, availableTours);

    if (isMobile) return null;

    //Owners that are mounted: these belong to what is on screen.
    const here = registered.filter(id => TOURS[id] && TOURS[id].scope !== "global");
    //Tours that live on another tab the user is allowed to reach.
    //
    //canSeeTab, NOT `can(tour.perm)`. A tab's permission is an any-of LIST, and
    //`can` takes its permissions as a rest parameter — so passing the list as
    //one argument asked whether the user held a permission named
    //`["messages.send"]`, which nobody does. That is not a hypothetical: it
    //shipped, and it silently emptied this section of every gated tour, leaving
    //a menu that offered only Inventory and Sales however many tours existed.
    const elsewhere = Object.entries(TOURS).filter(([id, tour]) =>
        tour.scope === "tab" && !registered.includes(id) && canSeeTab(tour.tab, can));

    const start = (id) => {
        if (startRegisteredTour(id)) return;
        const tab = TOURS[id]?.tab;
        if (!tab) return;
        //The owner is on another tab. Navigate on the channel CarsNav already
        //listens to, and arm the tour to fire when that subtree mounts.
        emitter.emit("navigate", { tab });
        requestTour(id);
    };

    return (
        <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
                <button
                    id="help-menu-button"
                    type="button"
                    aria-label="Help and tours"
                    title="Help and tours"
                    className="p-2 rounded-full text-gray-600 hover:bg-gray-200 transition-colors"
                >
                    <HelpCircle className="h-5 w-5" />
                </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content className="bg-white rounded-lg shadow-xl border border-gray-200 p-1 min-w-[220px]">
                <DropdownMenu.Item id="help-menu-overview" className={ITEM} onClick={() => start("overview")}>
                    App overview
                </DropdownMenu.Item>

                {here.length > 0 && (
                    <>
                        <DropdownMenu.Separator className="my-1 h-px bg-gray-200" />
                        <div className={LABEL}>This screen</div>
                        {here.map(id => (
                            <DropdownMenu.Item
                                key={id}
                                id={`help-menu-${id}`}
                                className={ITEM}
                                onClick={() => start(id)}
                            >
                                {TOURS[id].label}
                            </DropdownMenu.Item>
                        ))}
                    </>
                )}

                {elsewhere.length > 0 && (
                    <>
                        <DropdownMenu.Separator className="my-1 h-px bg-gray-200" />
                        <div className={LABEL}>Other screens</div>
                        {elsewhere.map(([id, tour]) => (
                            <DropdownMenu.Item
                                key={id}
                                id={`help-menu-${id}`}
                                className={ITEM}
                                onClick={() => start(id)}
                            >
                                {tour.label}
                            </DropdownMenu.Item>
                        ))}
                    </>
                )}

                <DropdownMenu.Separator className="my-1 h-px bg-gray-200" />
                {/* Opens the modal; the tour of it registers itself once open,
                    and appears above under "This screen". Distinct id from
                    help-menu-security, which is that tour. */}
                <DropdownMenu.Item id="help-menu-open-security" className={ITEM} onClick={onOpenSecurity}>
                    Passkeys &amp; devices…
                </DropdownMenu.Item>
            </DropdownMenu.Content>
        </DropdownMenu.Root>
    );
}
